import { useState, useEffect, useRef, useMemo } from "react";
import useLaborMasterDataInit, { LaborSelection } from "./useLaborMasterDataInit";
import { useLaborUpdate } from "./useLaborUpdate";
import { useLaborDatabaseSync } from "./useLaborDatabaseSync";

/**
 * useLabor (Orchestrator)
 * -----------------------
 *   1. useLaborMasterDataInit → loads data from server
 *   2. (useLaborUpdate)       → pure state updates on user actions (next step)
 *   3. (useLaborDatabaseSync) → syncs selectedValues to DB after every real change (next step)
 */
export function useLabor({ measurements }: { measurements: any[] }) {
    // ── State ────────────────────────────────────────────────────────────────
    const [laborSchema, setLaborSchema] = useState<any[]>([]);
    const [cart, setCart] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [priceRanges, setPriceRanges] = useState<Record<string, { min: number; max: number }>>({});
    const [selectedValues, setSelectedValues] = useState<Record<string, LaborSelection>>({});

    const syncReadyRef = useRef(false);

    // ── Sync ─────────────────────────────────────────────────────────────────
    const { initializeDbState, syncToDatabase } = useLaborDatabaseSync();

    useEffect(() => {
        if (!syncReadyRef.current) return;
        
        // Debouncing happens implicitly by how fast the user types but we could easily wrap this.
        // It uses the same pattern as materials.
        const timer = setTimeout(() => {
            void syncToDatabase(selectedValues);
        }, 500); // 500ms debounce
        
        return () => clearTimeout(timer);
    }, [selectedValues, syncToDatabase]);

    // ── Hook: Update ─────────────────────────────────────────────────────────
    const { handleQuantityUpdate, handleClearLabor } = useLaborUpdate({ setSelectedValues });

    // ── Hook: Init ───────────────────────────────────────────────────────────
    const { loadLaborData, fetchCartItems } = useLaborMasterDataInit({
        measurements,
        setLaborSchema,
        setCart,
        setPriceRanges,
        setIsLoading,
        initializeDbState,
        setSelectedValues: (values) => {
            setSelectedValues(values);
            syncReadyRef.current = true; // From now on, all changes will sync
        },
    });

    // ── Adapter for Supplier Comparison ──────────────────────────────────────
    const adaptedSelectedValues = useMemo(() => {
        const adapted: Record<string, any> = {};
        laborSchema.forEach((schemaItem) => {
            const selection = selectedValues[schemaItem.Id];
            if (selection && selection.quantity > 0) {
                adapted[schemaItem.Id] = {
                    id: selection.id || selection.CartItemId,
                    value: schemaItem.Name,         // backend 'ProductName' expects this value
                    supplierName: selection.supplierName,
                    supplierId: selection.supplierId,
                    quantity: selection.quantity,
                    unitPrice: selection.unitPrice
                };
            }
        });
        return adapted;
    }, [laborSchema, selectedValues]);

    // ── Expose ───────────────────────────────────────────────────────────────
    return {
        laborSchema,
        cart,
        priceRanges,
        isLoading,
        selectedValues,
        adaptedSelectedValues,
        loadLaborData,
        fetchCartItems,
        handleQuantityUpdate,
        handleClearLabor,
    };
}
